import { useCallback, useEffect, useState } from "react";
import type { Locale } from "../templates/types";
import { getVoiceCapabilities } from "./capabilities";

// §16: es-MX first, then es-US, then any Spanish voice, then whatever the
// browser defaults to — in that order. English just takes the default.
const FALLBACK_LANG: Record<Locale, string> = { en: "en-US", es: "es-MX" };

function pickVoice(locale: Locale, voices: SpeechSynthesisVoice[]): SpeechSynthesisVoice | null {
  if (locale !== "es") return null; // English: browser default is fine, no preference to express.
  return (
    voices.find((v) => v.lang === "es-MX") ??
    voices.find((v) => v.lang === "es-US") ??
    voices.find((v) => v.lang?.toLowerCase().startsWith("es")) ??
    null
  );
}

/**
 * Output side of §16. A plain imperative API: this hook never decides WHEN
 * to speak — that is entirely the caller's call (SafetyBanner's "Read aloud"
 * button, and nowhere else auto-invokes it). In particular the AI summary
 * and the fixed safety message are never spoken except in direct response to
 * an explicit button press; enforcing that is the caller's job; this hook
 * only makes sure IT never speaks on its own (no effect here calls speak()).
 */
export function useSpeech() {
  const [muted, setMuted] = useState(false);
  const canSpeak = getVoiceCapabilities().canSpeak;

  useEffect(() => {
    // Stop any in-flight utterance when the owning component unmounts —
    // nothing should keep talking after the UI that requested it is gone.
    return () => {
      window.speechSynthesis?.cancel();
    };
  }, []);

  const cancel = useCallback(() => {
    window.speechSynthesis?.cancel();
  }, []);

  const speak = useCallback(
    (text: string, locale: Locale) => {
      const synth = typeof window !== "undefined" ? window.speechSynthesis : undefined;
      if (!synth || muted || !text) return;

      // Cancel any in-flight utterance before starting a new one (§16).
      synth.cancel();

      const utterance = new SpeechSynthesisUtterance(text);
      const voices = synth.getVoices?.() ?? [];
      const voice = pickVoice(locale, voices);
      if (voice) {
        utterance.voice = voice;
        utterance.lang = voice.lang;
      } else {
        utterance.lang = FALLBACK_LANG[locale];
      }
      synth.speak(utterance);
    },
    [muted],
  );

  return { speak, cancel, muted, setMuted, canSpeak };
}
